import { formatDateTime } from '@/main/helpers'
import { i18nTranslationHelper } from '@/i18n'
import {
  AuthorizationPaginatedResponse,
  AuthorizationHistoryResponse,
  AuthorizationFilterParams,
  AuthorizationStatusGroup,
  AuthorizationLabel,
  AccountType,
  AuthorizationStatusEnum
} from './authorization-service-types'

// ==================== TYPES ====================

const t = i18nTranslationHelper.getTEnums

export type AuthorizationStatusType = {
  id: number
  name: string
  displayText: string
  type: 'default' | 'primary' | 'secondary' | 'info' | 'error' | 'warning' | 'success'
}

// ==================== STATUS DOMAIN MODEL ====================

const authorizationStatusMap: Record<number, AuthorizationStatusType> = {
  [AuthorizationStatusEnum.PENDING]: {
    id: AuthorizationStatusEnum.PENDING,
    name: 'PENDING',
    displayText: t('authorizationsStatus.PENDING.display'),
    type: 'info'
  },
  [AuthorizationStatusEnum.LEADER_REJECTED]: {
    id: AuthorizationStatusEnum.LEADER_REJECTED,
    name: 'LEADER_REJECTED',
    displayText: t('authorizationsStatus.LEADER_REJECTED.display'),
    type: 'error'
  },
  [AuthorizationStatusEnum.WAITING_ENROLLMENT]: {
    id: AuthorizationStatusEnum.WAITING_ENROLLMENT,
    name: 'WAITING_ENROLLMENT',
    displayText: t('authorizationsStatus.WAITING_ENROLLMENT.display'),
    type: 'warning'
  },
  [AuthorizationStatusEnum.ERROR_ENROLLMENT]: {
    id: AuthorizationStatusEnum.ERROR_ENROLLMENT,
    name: 'ERROR_ENROLLMENT',
    displayText: t('authorizationsStatus.ERROR_ENROLLMENT.display'),
    type: 'error'
  },
  [AuthorizationStatusEnum.ONGOING]: {
    id: AuthorizationStatusEnum.ONGOING,
    name: 'ONGOING',
    displayText: t('authorizationsStatus.ONGOING.display'),
    type: 'info'
  },
  [AuthorizationStatusEnum.ABANDONED]: {
    id: AuthorizationStatusEnum.ABANDONED,
    name: 'ABANDONED',
    displayText: t('authorizationsStatus.ABANDONED.display'),
    type: 'error'
  },
  [AuthorizationStatusEnum.FINISHED]: {
    id: AuthorizationStatusEnum.FINISHED,
    name: 'FINISHED',
    displayText: t('authorizationsStatus.FINISHED.display'),
    type: 'success'
  },
  [AuthorizationStatusEnum.EVALUATED]: {
    id: AuthorizationStatusEnum.EVALUATED,
    name: 'EVALUATED',
    displayText: t('authorizationsStatus.EVALUATED.display'),
    type: 'success'
  },
  [AuthorizationStatusEnum.WAITING_PAYMENT]: {
    id: AuthorizationStatusEnum.WAITING_PAYMENT,
    name: 'WAITING_PAYMENT',
    displayText: t('authorizationsStatus.WAITING_PAYMENT.display'),
    type: 'warning'
  },
  [AuthorizationStatusEnum.ERROR_PAYMENT]: {
    id: AuthorizationStatusEnum.ERROR_PAYMENT,
    name: 'ERROR_PAYMENT',
    displayText: t('authorizationsStatus.ERROR_PAYMENT.display'),
    type: 'error'
  },
  [AuthorizationStatusEnum.EXPIRED]: {
    id: AuthorizationStatusEnum.EXPIRED,
    name: 'EXPIRED',
    displayText: t('authorizationsStatus.EXPIRED.display'),
    type: 'error'
  },
  [AuthorizationStatusEnum.WAITING_REFUND]: {
    id: AuthorizationStatusEnum.WAITING_REFUND,
    name: 'WAITING_REFUND',
    displayText: t('authorizationsStatus.WAITING_REFUND.display'),
    type: 'warning'
  },
  [AuthorizationStatusEnum.WAITING_BOLETO]: {
    id: AuthorizationStatusEnum.WAITING_BOLETO,
    name: 'WAITING_BOLETO',
    displayText: t('authorizationsStatus.WAITING_BOLETO.display'),
    type: 'warning'
  },
  [AuthorizationStatusEnum.LEADER_APPROVED]: {
    id: AuthorizationStatusEnum.LEADER_APPROVED,
    name: 'LEADER_APPROVED',
    displayText: t('authorizationsStatus.LEADER_APPROVED.display'),
    type: 'success'
  },
  [AuthorizationStatusEnum.SENT_BOLETO]: {
    id: AuthorizationStatusEnum.SENT_BOLETO,
    name: 'SENT_BOLETO',
    displayText: t('authorizationsStatus.SENT_BOLETO.display'),
    type: 'info'
  },
  [AuthorizationStatusEnum.SENT_NF]: {
    id: AuthorizationStatusEnum.SENT_NF,
    name: 'SENT_NF',
    displayText: t('authorizationsStatus.SENT_NF.display'),
    type: 'info'
  },
  [AuthorizationStatusEnum.PROCESSING_REFUND]: {
    id: AuthorizationStatusEnum.PROCESSING_REFUND,
    name: 'PROCESSING_REFUND',
    displayText: t('authorizationsStatus.PROCESSING_REFUND.display'),
    type: 'info'
  },
  [AuthorizationStatusEnum.WAITING_SECOND_APPROVER]: {
    id: AuthorizationStatusEnum.WAITING_SECOND_APPROVER,
    name: 'WAITING_SECOND_APPROVER',
    displayText: t('authorizationsStatus.WAITING_SECOND_APPROVER.display'),
    type: 'warning'
  },
  [AuthorizationStatusEnum.WAITING_BUDGET_APPROVER]: {
    id: AuthorizationStatusEnum.WAITING_BUDGET_APPROVER,
    name: 'WAITING_BUDGET_APPROVER',
    displayText: t('authorizationsStatus.WAITING_BUDGET_APPROVER.display'),
    type: 'warning'
  },
  [AuthorizationStatusEnum.WAITING_INSTITUTION_PAYMENT]: {
    id: AuthorizationStatusEnum.WAITING_INSTITUTION_PAYMENT,
    name: 'WAITING_INSTITUTION_PAYMENT',
    displayText: t('authorizationsStatus.WAITING_INSTITUTION_PAYMENT.display'),
    type: 'warning'
  },
  [AuthorizationStatusEnum.WAITING_FINANCIAL_PASS_PAYMENT]: {
    id: AuthorizationStatusEnum.WAITING_FINANCIAL_PASS_PAYMENT,
    name: 'WAITING_FINANCIAL_PASS_PAYMENT',
    displayText: t('authorizationsStatus.WAITING_FINANCIAL_PASS_PAYMENT.display'),
    type: 'warning'
  }
}

export const AuthorizationStatus = {
  getById: (id: number): AuthorizationStatusType | undefined => {
    return authorizationStatusMap[id]
  },

  getByName: (name: string): AuthorizationStatusType | undefined => {
    return Object.values(authorizationStatusMap).find(status => status.name === name)
  },

  courseIsOnFreeFlowType: (flowType: number): boolean => {
    return flowType === 1 || flowType === 2
  }
}

// ==================== FORMATTERS ====================

// ==================== AUTHORIZATION FORMATTERS ====================

export const formatAuthorizationItem = (
  item: AuthorizationPaginatedResponse['results'][0]
): AuthorizationPaginatedResponse['results'][0] => {
  const rawItem = item as unknown as Record<string, unknown>

  // Parse labels
  const parsedLabels: AuthorizationLabel[] = Array.isArray(item.labels) ? item.labels : []

  // Map status
  const mappedStatusId = (rawItem.status ?? rawItem.statusId ?? -1) as number
  const currentStatus = AuthorizationStatus.getById(mappedStatusId)

  return {
    ...item,
    id: (rawItem.id ?? item.id) as number,
    authorizationId: (rawItem.id ?? item.authorizationId) as number,
    price: (rawItem.value ?? rawItem.price ?? 0) as number,
    statusId: mappedStatusId,
    statusCode: (rawItem.statusCode ?? item.statusCode ?? '') as string,
    statusName:
      currentStatus?.displayText ?? (((rawItem.statusName ?? item.statusName) as string) || ''),
    currentStatus,
    accountType: (rawItem.userAccountType ??
      rawItem.accountType ??
      AccountType.COLLABORATOR) as AccountType,
    labels: parsedLabels,
    createdAt: formatDateTime(item.createdAt || '') || '',
    updatedAt: formatDateTime(item.updatedAt || '') || '',
    startDate: item.startDate ? formatDateTime(item.startDate) : undefined,
    endDate: item.endDate ? formatDateTime(item.endDate) : undefined,
    userName: item.userName || '',
    userEmail: item.userEmail || '',
    companyName: item.companyName || '',
    institutionName: item.institutionName || '',
    courseName: item.courseName || '',
    isDependent: item.isDependent || false,
    isSkillPass: item.isSkillPass || false
  }
}

export const formatAuthorizationList = (
  response: AuthorizationPaginatedResponse
): AuthorizationPaginatedResponse => {
  if (!response || !Array.isArray(response.results)) {
    return {
      results: [],
      pages: response?.pages || 0,
      pageSize: response?.pageSize || 10,
      resultsQuantity: response?.resultsQuantity || 0
    }
  }

  return {
    ...response,
    results: response.results.map(item => formatAuthorizationItem(item))
  }
}

// ==================== HISTORY FORMATTERS ====================

export const formatAuthorizationHistory = (
  response: AuthorizationHistoryResponse
): AuthorizationHistoryResponse => {
  const rawHistory = response.authorizationHistoryList as unknown as Array<{
    id: number
    userId: number
    status: number
    comment: string
    attachment: string | null
    createdAt: string
    userName: string
    visible: boolean
    [key: string]: unknown
  }>

  const formattedHistory = rawHistory.map(item => {
    const statusInfo = AuthorizationStatus.getById(item.status)

    return {
      id: item.id,
      authorizationId: response.authorizationInfo.id,
      userId: item.userId,
      userName: item.userName,
      userPhoto: response.authorizationInfo.userPhoto,
      comment: item.comment || '',
      statusId: item.status,
      statusName: statusInfo?.displayText || '',
      isVisible: item.visible ?? true,
      isNf: false,
      attachmentUrl: item.attachment || undefined,
      createdAt: item.createdAt ? formatDateTime(item.createdAt) || '' : '',
      rating: undefined,
      tag: undefined
    }
  })

  return {
    ...response,
    authorizationHistoryList: formattedHistory
  }
}

// ==================== QUERY & FORM UTILITIES ====================

export const buildFilterQueryParams = (
  params: AuthorizationFilterParams
): Record<string, string> => {
  const queryParams: Record<string, string> = {}

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams[key] = String(value)
    }
  })

  return queryParams
}

export const createFormDataWithFile = (data: Record<string, unknown>): FormData => {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value instanceof File ? value : String(value))
    }
  })

  return formData
}

// ==================== DATA UTILITIES ====================

export const normalizeFilterOption = (
  option: Record<string, unknown>
): { id: number | string; name: string } => ({
  id: (option.id as number) || (option.value as number | string),
  name: (option.displayName as string) || (option.name as string) || (option.label as string) || ''
})

export const normalizeFilterOptions = (
  options: Array<{ [key: string]: unknown }>
): Array<{ id: number | string; name: string }> => options.map(normalizeFilterOption)

export const formatStatusGroups = (
  rawData: Array<{ [key: string]: unknown }>
): AuthorizationStatusGroup[] =>
  rawData.map(group => ({
    id: (group.id as number) || 0,
    name: (group.name as string) || '',
    displayName: (group.displayName as string) || (group.name as string) || '',
    visible:
      group.visibility !== undefined
        ? (group.visibility as boolean)
        : group.visible !== undefined
          ? (group.visible as boolean)
          : true,
    statusIds: (group.statusIds as number[]) || [],
    messageKey: (group.messageKey as string) || undefined
  }))
